/**
 * The supply picture: what the unit has, what it cannot fly, and what that costs.
 *
 * A store rather than a `useState` in the screen for the same reason
 * missionStore is: the map layer draws depots and forward sites without React,
 * and a hook that only exists while the Supply screen is mounted cannot feed a
 * layer on Command or Planning. `subscribeToSupplyLocations` is that seam.
 *
 * Two loads, deliberately separate:
 *
 *   `overview`   the commander's one call. Polled, and the only thing the
 *                headline reads. It arrives whole or not at all, so the page
 *                cannot render readiness from one response and impacts from an
 *                older one.
 *   `reference`  parts, vendors, locations, platforms, assets, profiles,
 *                orders, stock. Slow-moving, loaded once, and only the lower
 *                detail section reads it.
 *
 * `etag` is carried from whichever read last returned one, because a stock edit
 * has to be made against a version. Sending no `ifMatch` would let two forward
 * operators silently overwrite each other's counts, which is exactly the class
 * of error an unverified count already represents.
 */

import { create } from 'zustand';

import {
  getAssets,
  getLocations,
  getOrders,
  getParts,
  getPlatforms,
  getProfiles,
  getStock,
  getSupplyOverview,
  getVendors,
  postFeasibility,
  postImport,
  setStock as setStockApi,
  type Asset,
  type FeasibilityRequest,
  type FeasibilityResult,
  type ImportReport,
  type ImportRequest,
  type Part,
  type Platform,
  type SetStockRequest,
  type SortieProfile,
  type StockItem,
  type SupplyLocation,
  type SupplyOrder,
  type SupplyOverview,
  type Vendor,
} from '@/api/supply';

/** One shared empty array per collection, so "nothing loaded" has a stable identity. */
const NO_LOCATIONS: SupplyLocation[] = [];
const NO_STOCK: StockItem[] = [];
const NO_PARTS: Part[] = [];
const NO_VENDORS: Vendor[] = [];
const NO_PLATFORMS: Platform[] = [];
const NO_ASSETS: Asset[] = [];
const NO_PROFILES: SortieProfile[] = [];
const NO_ORDERS: SupplyOrder[] = [];

interface SupplyStoreState {
  overview: SupplyOverview | null;
  /** Sorties one serviceable aircraft is assumed to fly per day. Drives the cost figures. */
  sortiesPerAssetPerDay: number;

  stock: StockItem[];
  parts: Part[];
  vendors: Vendor[];
  locations: SupplyLocation[];
  platforms: Platform[];
  assets: Asset[];
  profiles: SortieProfile[];
  orders: SupplyOrder[];

  /** The version the next stock write is made against. */
  etag: string | null;

  loading: boolean;
  /** True only for the first load, so a poll does not blank the screen. */
  referenceLoaded: boolean;
  error: string | null;

  /** The last feasibility answer, and whether one is in flight. */
  feasibility: FeasibilityResult | null;
  feasibilityBusy: boolean;

  /** The last import check or import. Null until one has been run. */
  importReport: ImportReport | null;
  importBusy: boolean;

  loadOverview: () => Promise<void>;
  loadReference: () => Promise<void>;
  setSortiesPerAssetPerDay: (rate: number) => void;
  askFeasibility: (request: FeasibilityRequest) => Promise<void>;
  clearFeasibility: () => void;
  saveStock: (request: Omit<SetStockRequest, 'ifMatch'>) => Promise<{ ok: boolean; message: string }>;
  /**
   * Check or apply an import. `report` is null only when the call never reached
   * a verdict — a transport failure or a 409 — and `error` says which. A report
   * with `ok: false` is a verdict: the server refused and wrote nothing.
   */
  runImport: (
    request: Omit<ImportRequest, 'ifMatch'>,
  ) => Promise<{ report: ImportReport | null; error: string | null }>;
  clearImport: () => void;
  reset: () => void;
}

export const useSupplyStore = create<SupplyStoreState>((set, get) => ({
  overview: null,
  sortiesPerAssetPerDay: 1.0,

  stock: NO_STOCK,
  parts: NO_PARTS,
  vendors: NO_VENDORS,
  locations: NO_LOCATIONS,
  platforms: NO_PLATFORMS,
  assets: NO_ASSETS,
  profiles: NO_PROFILES,
  orders: NO_ORDERS,

  etag: null,
  loading: false,
  referenceLoaded: false,
  error: null,

  feasibility: null,
  feasibilityBusy: false,

  importReport: null,
  importBusy: false,

  loadOverview: async () => {
    set({ loading: true });
    const result = await getSupplyOverview(get().sortiesPerAssetPerDay);
    if (!result.ok) {
      // The previous overview is left in place. Blanking a commander's readiness
      // figures because one poll timed out is worse than showing the last known
      // picture next to the error that says it is the last known picture.
      set({ loading: false, error: result.error.message });
      return;
    }
    set({ loading: false, error: null, overview: result.data, etag: result.data.etag });
  },

  loadReference: async () => {
    const [stock, parts, vendors, locations, platforms, assets, profiles, orders] =
      await Promise.all([
        getStock(),
        getParts(),
        getVendors(),
        getLocations(),
        getPlatforms(),
        getAssets(),
        getProfiles(),
        getOrders(),
      ]);

    set((state) => ({
      referenceLoaded: true,
      stock: stock.ok ? stock.data.stock : state.stock,
      etag: stock.ok ? stock.data.etag : state.etag,
      parts: parts.ok ? parts.data.parts : state.parts,
      vendors: vendors.ok ? vendors.data.vendors : state.vendors,
      locations: locations.ok ? locations.data.locations : state.locations,
      platforms: platforms.ok ? platforms.data.platforms : state.platforms,
      assets: assets.ok ? assets.data.assets : state.assets,
      profiles: profiles.ok ? profiles.data.profiles : state.profiles,
      orders: orders.ok ? orders.data.orders : state.orders,
    }));
  },

  setSortiesPerAssetPerDay: (rate) => {
    set({ sortiesPerAssetPerDay: rate });
    void get().loadOverview();
  },

  askFeasibility: async (request) => {
    set({ feasibilityBusy: true });
    const result = await postFeasibility(request);
    if (!result.ok) {
      set({ feasibilityBusy: false, feasibility: null, error: result.error.message });
      return;
    }
    set({ feasibilityBusy: false, feasibility: result.data, error: null });
  },

  clearFeasibility: () => set({ feasibility: null }),

  saveStock: async (request) => {
    const ifMatch = get().etag;
    const result = await setStockApi(ifMatch === null ? request : { ...request, ifMatch });
    if (!result.ok) {
      // A 409 is not a bug, it is somebody else having counted the same shelf.
      // Say so, and reload rather than retrying over their number.
      const message =
        result.error.kind === 'conflict'
          ? 'Somebody else changed stock since this screen loaded. Reloaded — check the count and try again.'
          : result.error.message;
      if (result.error.kind === 'conflict') {
        await get().loadReference();
        await get().loadOverview();
      }
      set({ error: message });
      return { ok: false, message };
    }
    set({ etag: result.data.etag, error: null });
    await get().loadReference();
    await get().loadOverview();
    return { ok: true, message: 'Stock updated.' };
  },

  runImport: async (request) => {
    set({ importBusy: true });
    const ifMatch = get().etag;
    const result = await postImport(ifMatch === null ? request : { ...request, ifMatch });

    if (!result.ok) {
      // A 409 means the catalog moved between the check and the import. The
      // check described a catalog that no longer exists, so it is discarded
      // rather than retried on top of somebody else's change — a retry here
      // would apply a file that was approved against different data.
      const conflict = result.error.kind === 'conflict';
      const message = conflict
        ? 'The catalog changed since this file was checked. Nothing was imported. Reloaded — check the file again against the current catalog.'
        : result.error.message;
      if (conflict) {
        await get().loadReference();
        await get().loadOverview();
      }
      // Deliberately not the screen-level `error`: the import panel is where
      // this has to be read, and a second red banner above it saying the same
      // thing reads as two failures.
      set({ importBusy: false, importReport: null });
      return { report: null, error: message };
    }

    const report = result.data;
    set({
      importBusy: false,
      importReport: report,
      // A refused import is not a screen-level error: the panel says precisely
      // what is wrong with the file, and a red banner above it would only
      // repeat that less usefully.
      error: null,
      ...(report.etag ? { etag: report.etag } : {}),
    });

    // Only a real write moves the catalog. A dry run and a refusal both leave
    // it exactly as it was, and reloading after those would suggest otherwise.
    if (report.written) {
      await get().loadReference();
      await get().loadOverview();
    }
    return { report, error: null };
  },

  clearImport: () => set({ importReport: null }),

  reset: () =>
    set({
      overview: null,
      stock: NO_STOCK,
      parts: NO_PARTS,
      vendors: NO_VENDORS,
      locations: NO_LOCATIONS,
      platforms: NO_PLATFORMS,
      assets: NO_ASSETS,
      profiles: NO_PROFILES,
      orders: NO_ORDERS,
      etag: null,
      loading: false,
      referenceLoaded: false,
      error: null,
      feasibility: null,
      feasibilityBusy: false,
      importReport: null,
      importBusy: false,
    }),
}));

/**
 * Watch the supply sites without React, the way the mission layer watches
 * routes. Fires only when the location list or the stock behind it changes
 * identity — a busy flag or a new feasibility answer must not redraw the map.
 */
export function subscribeToSupplyLocations(
  callback: (picture: { locations: SupplyLocation[]; stock: StockItem[]; parts: Part[] }) => void,
): () => void {
  let previous = useSupplyStore.getState();
  callback({ locations: previous.locations, stock: previous.stock, parts: previous.parts });

  return useSupplyStore.subscribe((state) => {
    if (
      state.locations === previous.locations &&
      state.stock === previous.stock &&
      state.parts === previous.parts
    ) {
      return;
    }
    previous = state;
    callback({ locations: state.locations, stock: state.stock, parts: state.parts });
  });
}

/**
 * Actions at module scope, stable forever. A component that reaches one action
 * through `useSupplyStore()` has subscribed to every field in this file.
 */
export const supplyActions = {
  loadOverview: () => useSupplyStore.getState().loadOverview(),
  loadReference: () => useSupplyStore.getState().loadReference(),
  setSortiesPerAssetPerDay: (rate: number) =>
    useSupplyStore.getState().setSortiesPerAssetPerDay(rate),
  askFeasibility: (request: FeasibilityRequest) =>
    useSupplyStore.getState().askFeasibility(request),
  clearFeasibility: () => useSupplyStore.getState().clearFeasibility(),
  saveStock: (request: Omit<SetStockRequest, 'ifMatch'>) =>
    useSupplyStore.getState().saveStock(request),
  runImport: (request: Omit<ImportRequest, 'ifMatch'>) =>
    useSupplyStore.getState().runImport(request),
  clearImport: () => useSupplyStore.getState().clearImport(),
  reset: () => useSupplyStore.getState().reset(),
};

/* Selectors. One question each, so a stock edit does not re-render the risk
 * panel and a new feasibility answer does not re-render the impact list. */

export function useSupplyOverview(): SupplyOverview | null {
  return useSupplyStore((state) => state.overview);
}

export function useSupplyLoading(): boolean {
  return useSupplyStore((state) => state.loading);
}

export function useSupplyError(): string | null {
  return useSupplyStore((state) => state.error);
}

export function useSupplyLocations(): SupplyLocation[] {
  return useSupplyStore((state) => state.locations);
}

export function useSupplyStock(): StockItem[] {
  return useSupplyStore((state) => state.stock);
}

export function useSupplyParts(): Part[] {
  return useSupplyStore((state) => state.parts);
}

export function useSupplyVendors(): Vendor[] {
  return useSupplyStore((state) => state.vendors);
}

export function useSupplyPlatforms(): Platform[] {
  return useSupplyStore((state) => state.platforms);
}

export function useSupplyAssets(): Asset[] {
  return useSupplyStore((state) => state.assets);
}

export function useSupplyProfiles(): SortieProfile[] {
  return useSupplyStore((state) => state.profiles);
}

export function useSupplyOrders(): SupplyOrder[] {
  return useSupplyStore((state) => state.orders);
}

export function useSupplyFeasibility(): { result: FeasibilityResult | null; busy: boolean } {
  const result = useSupplyStore((state) => state.feasibility);
  const busy = useSupplyStore((state) => state.feasibilityBusy);
  return { result, busy };
}

export function useSupplyImport(): { report: ImportReport | null; busy: boolean } {
  const report = useSupplyStore((state) => state.importReport);
  const busy = useSupplyStore((state) => state.importBusy);
  return { report, busy };
}
